import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom'
import ErrorBoundary from './components/ErrorBoundary'
import PatientList from './components/PatientList'
import AddPatient from './components/AddPatient'
import PatientDashboard from './components/PatientDashboard'
import Login from './components/Login'
import PatientView from './components/PatientView'
import Settings from './components/Settings'
import ClinicSummary from './components/ClinicSummary'
import Appointments from './components/Appointments'
import IntakeForm from './components/IntakeForm'
import ResetPassword from './components/ResetPassword'
import SuperAdmin from './components/SuperAdmin'
import LandingPage from './components/LandingPage'
import PrivacyPolicy from './components/PrivacyPolicy'
import TermsOfService from './components/TermsOfService'
import BusinessAssociateAgreement from './components/BusinessAssociateAgreement'
import StaffManagement from './components/StaffManagement'
import InjuryExplorer from './components/InjuryExplorer'
import Analytics from './components/Analytics'
import SetupWizard from './components/SetupWizard'
import { CalebProvider } from './context/CalebContext'
import CalebPanel from './components/CalebPanel'
import SessionTimeoutManager from './components/SessionTimeoutManager'
import { LanguageProvider } from './context/LanguageContext'
import LanguageSwitcher from './components/LanguageSwitcher'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

//  JWT helpers 
const TOKEN_KEY = 'clearpath_token'
export const getToken  = ()        => localStorage.getItem(TOKEN_KEY)
export const saveToken = (t)       => localStorage.setItem(TOKEN_KEY, t)
export const clearToken = ()       => {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem('clearpath_user')
}

// Module-level user ref — updated on login/logout so axios interceptor can read it
// without needing access to React state.
let _axiosUser = null
export const setAxiosUser = (u) => { _axiosUser = u }

// Attach JWT + HIPAA audit-identity headers to every axios request automatically
axios.interceptors.request.use(cfg => {
  const token = getToken()
  if (token) cfg.headers['Authorization'] = `Bearer ${token}`
  // Send identity headers so the backend audit-log middleware can record WHO
  // made each request (clinic_id, user_id, username). Without these the audit
  // table logs "unknown" for every entry.
  if (_axiosUser) {
    if (_axiosUser.clinic_id != null) cfg.headers['X-Clinic-ID'] = String(_axiosUser.clinic_id)
    if (_axiosUser.id        != null) cfg.headers['X-User-ID']   = String(_axiosUser.id)
    if (_axiosUser.username)          cfg.headers['X-Username']  = _axiosUser.username
  }
  return cfg
})
// Silent JWT refresh — if token is within 30 minutes of expiry, swap it before the request goes out
let _refreshing = false
axios.interceptors.request.use(async cfg => {
  const token = getToken()
  if (token && !cfg.url?.includes('/auth/')) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const expiresAt = payload.exp * 1000
      const thirtyMin = 30 * 60 * 1000
      if (expiresAt - Date.now() < thirtyMin && !_refreshing) {
        _refreshing = true
        const res = await axios.post(API + '/auth/refresh', {}, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.data?.token) saveToken(res.data.token)
        _refreshing = false
      }
    } catch { _refreshing = false }
  }
  return cfg
})

// Auto-logout on 401 (expired / invalid token)
// Uses ?expired=1 URL param so MainApp goes straight to login, not landing.
axios.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401 && !err.config?.url?.includes("/auth/")) {
      clearToken()
      setAxiosUser(null)
      const url = new URL(window.location.href)
      url.searchParams.set('expired', '1')
      window.location.replace(url.toString())
    }
    return Promise.reject(err)
  }
)
const TEAL = '#0d9488'
const TEAL_DARK = '#0f766e'
const PURPLE      = '#7c3aed'
const PURPLE_DARK = '#6d28d9'
const PURPLE_MID  = '#c4b5fd'
const PURPLE_LIGHT= '#f5f3ff'

function IntakeFormWrapper() {
  const { token } = useParams()
  return <IntakeForm token={token} />
}

function ResetPasswordWrapper() {
  const { token } = useParams()
  return <ResetPassword token={token} />
}

function Toast({ toasts, removeToast }) {
  return (
    <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          background: t.type === 'success' ? '#f0fdf4' : t.type === 'error' ? '#fef2f2' : '#f8fafc',
          border: '1px solid ' + (t.type === 'success' ? '#bbf7d0' : t.type === 'error' ? '#fecaca' : '#e2e8f0'),
          color: t.type === 'success' ? '#15803d' : t.type === 'error' ? '#dc2626' : '#334155',
          borderRadius: '10px', padding: '12px 16px', fontSize: '14px', fontWeight: '500',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxWidth: '320px', display: 'flex',
          alignItems: 'center', gap: '10px', fontFamily: FONT, animation: 'slideIn 0.2s ease'
        }}>
          <span style={{ flex: 1 }}>{t.message}</span>
          <button onClick={() => removeToast(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: 'inherit', opacity: 0.5, padding: 0, lineHeight: 1 }}>×</button>
        </div>
      ))}
    </div>
  )
}

function CreatePatientLogin({ clinicId }) {
  const [form, setForm] = useState({ patient_id: '', username: '', password: '' })
  const [message, setMessage] = useState('')
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value })
  const handleSubmit = () => {
    if (!form.patient_id || !form.username || !form.password) { setMessage('Please fill in all fields.'); return }
    // Use axios so the JWT interceptor attaches the Authorization header automatically
    axios.post(API + '/auth/create-user', {
      ...form,
      patient_id: parseInt(form.patient_id),
      role: 'patient',
      clinic_id: clinicId,
    })
      .then(res => setMessage(res.data.success ? 'Login created successfully!' : 'Error: ' + res.data.detail))
      .catch(err => setMessage('Error: ' + (err.response?.data?.detail || 'Something went wrong.')))
  }
  return (
    <div style={{ maxWidth: '480px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Create Patient Login</h2>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '6px' }}>Give a patient access to their recovery dashboard.</p>
      </div>
      <div style={{ background: 'white', borderRadius: '14px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Patient ID</label>
            <input name="patient_id" placeholder="e.g. 1" type="number" value={form.patient_id} onChange={handleChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Username</label>
            <input name="username" placeholder="e.g. sarahjohnson" value={form.username} onChange={handleChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Password</label>
            <input name="password" type="password" placeholder="Create a password" value={form.password} onChange={handleChange} style={inputStyle} />
          </div>
          {message && (
            <div style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '14px', background: message.includes('success') ? '#f0fdf4' : '#fef2f2', color: message.includes('success') ? '#15803d' : '#dc2626', border: '1px solid ' + (message.includes('success') ? '#bbf7d0' : '#fecaca') }}>
              {message}
            </div>
          )}
          <button onClick={handleSubmit} style={{ padding: '12px', backgroundColor: TEAL, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '15px', fontWeight: '600', fontFamily: FONT }}>
            Create Login
          </button>
        </div>
      </div>
    </div>
  )
}

//  Feature gate helper 
// Returns true if the clinic has access to a feature.
// Trial / legacy clinics with no features_enabled list get everything.
// Clinics with a list only get what they paid for (plus always-on base features).
const BASE_FEATURES = ['soap','intake','appointments','charts','messaging']
function hasFeature(user, featureId) {
  if (!user) return false
  const features = user.features_enabled
  // No list = trial or legacy clinic = full access
  if (!features || features.length === 0) return true
  if (BASE_FEATURES.includes(featureId)) return true
  return features.includes(featureId)
}

function FeatureLocked({ name, onUpgrade }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      minHeight:200, gap:12, color:'#94a3b8', textAlign:'center', padding:'40px 24px' }}>
      <div style={{ fontSize:36 }}></div>
      <div style={{ fontSize:16, fontWeight:700, color:'#475569' }}>{name}</div>
      <div style={{ fontSize:13, maxWidth:260 }}>This feature isn't included in your plan. Upgrade to unlock it.</div>
      {onUpgrade && <button onClick={onUpgrade}
        style={{ marginTop:8, padding:'9px 22px', background:'#0d9488', color:'white',
          border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
        Upgrade Plan
      </button>}
    </div>
  )
}

//  Payment Failed Banner 
// Shown to clinic admins when Stripe couldn't charge their card.
// The clinic stays active during Stripe's retry window (up to 4 attempts),
// but the admin needs to update their payment method before the subscription is cancelled.
function PaymentFailedBanner({ clinicId }) {
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null
  return (
    <div style={{
      background: 'linear-gradient(90deg, #92400e, #b45309)',
      padding: '10px 20px', display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 12, fontFamily: FONT, flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 16 }}></span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>
        Your last payment failed. Please update your payment method to avoid losing access.
      </span>
      <a
        href="https://clearpath-pt.com/#pricing"
        style={{
          padding: '5px 14px', borderRadius: 7, background: 'white',
          color: '#92400e', fontSize: 13, fontWeight: 700, textDecoration: 'none',
          flexShrink: 0,
        }}
      >
        Update payment →
      </a>
      <button
        onClick={() => setDismissed(true)}
        style={{
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)',
          cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px',
          marginLeft: 4, flexShrink: 0,
        }}
        title="Dismiss"
      >×</button>
    </div>
  )
}

function MainApp() {
  const [currentPage, setCurrentPage] = useState('summary')
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [pendingAppts, setPendingAppts] = useState(0)
  const [clinicName, setClinicName] = useState('ClearPath PT')
  const [toasts, setToasts] = useState([])
  const [patientViewUser, setPatientViewUser] = useState(null)
  const [showLanding, setShowLanding] = useState(true)
  const [showLogin, setShowLogin] = useState(false)
  const [loginInitialView, setLoginInitialView] = useState('login')
  // Demo role switcher — must be at top level (before any early returns) per React rules
  const [demoRole, setDemoRole] = useState('admin')
  const [demoPatientUser, setDemoPatientUser] = useState(null)
  const [demoPatientLoading, setDemoPatientLoading] = useState(false)
  const [showWizard, setShowWizard] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [pendingPriceSelection, setPendingPriceSelection] = useState(null)
  const [loginError, setLoginError] = useState('');
  const [initialClinicSlug, setInitialClinicSlug] = useState('')

  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now()
    setToasts(t => [...t, { id, message, type }])
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000)
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(t => t.filter(x => x.id !== id))
  }, [])

  useEffect(() => {
    const ping = () => axios.get(API + '/').catch(() => {})
    const interval = setInterval(ping, 10 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  // If URL contains ?clinic=SLUG, skip landing and pre-fill the clinic code
  // If URL contains ?expired=1, session expired — go directly to login (not landing)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const slug    = params.get('clinic')
    const expired = params.get('expired')
    if (slug) {
      setInitialClinicSlug(slug.toUpperCase())
      setShowLanding(false)
      setShowLogin(true)
    }
    if (expired === '1') {
      setShowLanding(false)
      setShowLogin(true)
      // Clean up URL so refreshing again doesn't loop
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  // If visiting a clinic subdomain (e.g. demo.clearpath-pt.com), auto-detect the slug
  useEffect(() => {
    const hostname = window.location.hostname
    const match = hostname.match(/^([^.]+)\.clearpath-pt\.com$/)
    if (match) {
      const subSlug = match[1].toUpperCase()
      setInitialClinicSlug(subSlug)
      setShowLanding(false)
      setShowLogin(true)
    }
  }, [])


  useEffect(() => {
    if (currentUser?.clinic_id || currentUser?.role === 'therapist') {
      axios.get(API + '/settings' + (currentUser.clinic_id ? '?clinic_id=' + currentUser.clinic_id : ''))
        .then(res => setClinicName(res.data.clinic_name || 'ClearPath PT'))
        .catch(() => {})
    }
  }, [currentUser])

  useEffect(() => {
    if (currentUser && ['admin', 'therapist', 'front_desk'].includes(currentUser.role)) {
      const check = () => {
        axios.get(API + '/appointments' + (currentUser.clinic_id ? '?clinic_id=' + currentUser.clinic_id : ''))
          .then(res => setPendingAppts(res.data.filter(a => a.status === 'requested').length))
          .catch(() => {})
      }
      check()
      const interval = setInterval(check, 15000)
      return () => clearInterval(interval)
    }
  }, [currentUser])

  const handleLogin = (user, token) => {
    if (token) saveToken(token)
    setAxiosUser(user)   // keep axios interceptor in sync for audit-log headers
    setCurrentUser(user)
    setShowLanding(false)
    setShowLogin(false)
    // Show setup wizard for first-time admins only
    // Guard: role must be admin, setup_complete must be false from backend,
    // AND no localStorage dismissal flag (so wizard never re-fires after first completion)
    const wizardDismissed = localStorage.getItem(`wizard_done_${user.clinic_id || user.id}`)
    if (user.role === 'admin' && !user.setup_complete && !wizardDismissed && user.username !== 'demo') {
      setShowWizard(true)
    }
    // Front desk lands on Appointments; everyone else on Overview
    if (user.role === 'front_desk') setCurrentPage('appointments')
    const params = new URLSearchParams(window.location.search)
    if (params.get('billing') === 'success') setCurrentPage('settings')
    // If user came from PricingBuilder, send them to settings so the upgrade modal can open
    if (pendingPriceSelection && user.role === 'admin') setCurrentPage('settings')
  }

  const handleLogout = () => {
    clearToken()
    setAxiosUser(null)
    setCurrentUser(null)
    setCurrentPage('summary')
    setPatientViewUser(null)
    setShowLanding(true)
    setShowLogin(false)
    setLoginInitialView('login')
  }

  const goToPatient = (patient) => { setSelectedPatient(patient); setCurrentPage('dashboard') }

  const handleDemo = async () => {
    // Helper: attempt login as the demo user. Returns {user, token} on success, null otherwise.
    const tryLogin = async () => {
      try {
        const res = await fetch(API + '/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: 'demo', password: 'demo123', clinic_slug: 'DEMO' })
        })
        if (!res.ok) return null
        const data = await res.json()
        if (data && data.user && data.token) return data
        return null
      } catch {
        return null
      }
    }

    // First attempt — works if demo account is already seeded (warm server)
    let data = await tryLogin()

    // If login failed, seed demo data then retry (handles cold-start case where
    // /demo/setup hadn't been run yet on this server). Retry a couple of times
    // with short backoff to give the DB seeding time to finish.
    if (!data) {
      try {
        await fetch(API + '/demo/setup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })
      } catch {
        // Non-fatal — demo user may already exist, or CORS preflight may have stalled
      }
      for (let i = 0; i < 3 && !data; i++) {
        await new Promise(r => setTimeout(r, 500))
        data = await tryLogin()
      }
    }

    if (data) {
      handleLogin(data.user, data.token)
    } else {
      alert('Demo could not be loaded. Please check your connection and try again, or contact support.')
    }
  }

  // Show landing
  if (!currentUser && showLanding && !showLogin) return (
    <LandingPage
      onGetStarted={(priceData) => {
        if (priceData && priceData.amount_dollars) setPendingPriceSelection(priceData)
        setLoginInitialView('register')
        setShowLanding(false)
        setShowLogin(true)
      }}
      onSignIn={() => { setLoginInitialView('login'); setShowLanding(false); setShowLogin(true) }}
      onDemo={handleDemo}
    />
  )

  // Show login/register
  if (!currentUser) return (
    <Login
      onLogin={handleLogin}
      onBackToHome={initialClinicSlug ? null : () => { setShowLanding(true); setShowLogin(false) }}
      initialView={loginInitialView}
      initialClinicSlug={initialClinicSlug}
    />
  )

  if (currentUser.role === 'patient') return (
    <>
      <SessionTimeoutManager user={currentUser} onLogout={handleLogout} />
      <PatientView user={currentUser} onLogout={handleLogout} />
    </>
  )
  if (currentUser.role === 'superadmin') return (
    <>
      <SessionTimeoutManager user={currentUser} onLogout={handleLogout} />
      <SuperAdmin onLogout={handleLogout} />
    </>
  )

  // First-time admin setup wizard (renders as overlay on top of the app)
  // We include it here so the underlying app is already mounted when wizard completes

  if (patientViewUser) return (
    <div>
      <div style={{ background: '#f59e0b', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: FONT }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: 'white' }}>Viewing as patient: {patientViewUser.username}</span>
        <button onClick={() => setPatientViewUser(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: FONT }}>← Back to Dashboard</button>
      </div>
      <PatientView user={patientViewUser} onLogout={() => setPatientViewUser(null)} />
    </div>
  )

  const isDemo      = currentUser.username === 'demo'
  const clinicId    = currentUser.clinic_id
  const isAdmin     = currentUser.role === 'admin'
  const isFrontDesk = currentUser.role === 'front_desk'

  // Demo role switcher — lets demo users preview each role's view
  const effectiveRole = isDemo ? demoRole : currentUser.role
  const effectiveUser        = currentUser   // feature flags always come from the real logged-in user
  const effectiveIsAdmin     = effectiveRole === 'admin'
  const effectiveIsFrontDesk = effectiveRole === 'front_desk'

  // Build nav based on effective role — hide locked features from nav
  const canInjuryExplorer = hasFeature(effectiveUser, 'injury3d')
  const canStaff          = hasFeature(effectiveUser, 'staff')

  const navItems = effectiveIsAdmin ? [
    { id: 'summary',          label: 'Overview' },
    { id: 'list',             label: 'Patients' },
    { id: 'appointments',     label: 'Appointments', badge: pendingAppts },
    { id: 'analytics',        label: 'Analytics' },
    ...(canInjuryExplorer ? [{ id: 'injury-explorer', label: ' Injury Explorer' }] : []),
    { id: 'add',              label: 'Add Patient' },
    { id: 'create-login',     label: 'Patient Logins' },
    ...(canStaff ? [{ id: 'staff', label: 'Staff' }] : []),
    { id: 'settings',         label: 'Settings' },
  ] : effectiveIsFrontDesk ? [
    { id: 'appointments', label: 'Appointments', badge: pendingAppts },
    { id: 'list',         label: 'Patients' },
    { id: 'add',          label: 'Add Patient' },
  ] : [
    // therapist
    { id: 'summary',         label: 'Overview' },
    { id: 'list',            label: 'Patients' },
    { id: 'appointments',    label: 'Appointments', badge: pendingAppts },
    { id: 'analytics',       label: 'Analytics' },
    ...(canInjuryExplorer ? [{ id: 'injury-explorer', label: ' Injury Explorer' }] : []),
    { id: 'add',             label: 'Add Patient' },
  ]

  const roleLabel = effectiveRole === 'admin' ? 'Admin' : effectiveRole === 'front_desk' ? 'Front Desk' : 'Therapist'
  const roleBadgeColor = effectiveRole === 'admin' ? '#99f6e4' : effectiveRole === 'front_desk' ? '#ddd6fe' : '#bfdbfe'

  return (
    <div style={{ fontFamily: FONT, minHeight: '100vh', background: 'transparent' }}>
      {/* HIPAA: auto-logout after 15 min inactivity, 2-min warning modal */}
      <SessionTimeoutManager user={currentUser} onLogout={handleLogout} />
      <style>{`
        * { box-sizing: border-box; }
        input, select, textarea, button { font-family: ${FONT}; }
        .nav-item:hover { background: rgba(255,255,255,0.08) !important; }

        /* ── ClearPath theme: gradient-border cards, no shadows ── */
        .cp-card {
          background: rgba(255,255,255,0.92) !important;
          border-radius: 16px !important;
          border: none !important;
          box-shadow: none !important;
          position: relative;
          overflow: hidden;
        }
        .cp-card::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 16px;
          padding: 1.5px;
          background: linear-gradient(135deg, #0d9488, #7c3aed);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
          z-index: 1;
        }
        /* Stat grid: replace solid bg separator with card-per-cell look */
        .cs-stat-grid {
          background: transparent !important;
          gap: 12px !important;
        }
        .cs-stat-card {
          background: rgba(255,255,255,0.92) !important;
          border-radius: 14px !important;
          box-shadow: none !important;
          position: relative;
          overflow: hidden;
        }
        .cs-stat-card::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 14px;
          padding: 1.5px;
          background: linear-gradient(135deg, #0d9488, #7c3aed);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
        }
        /* Patient list / table wrapper */
        .cs-patient-table {
          background: rgba(255,255,255,0.92) !important;
          border: none !important;
          box-shadow: none !important;
          border-radius: 14px !important;
          position: relative;
          overflow: hidden;
        }
        .cs-patient-table::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 14px;
          padding: 1.5px;
          background: linear-gradient(135deg, #0d9488, #7c3aed);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
        }
        @keyframes slideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .app-hamburger { display: none !important; }
        .app-nav-links { display: flex; align-items: center; gap: 2px; flex: 1; min-width: 0; }
        .app-user-label { display: block; }
        .app-mobile-nav { display: none; }
        /* Large monitors (1440px+) — stretch content to fill screen */
        @media (min-width: 1440px) {
          .app-main-pad { max-width: 1440px !important; padding: 36px 48px !important; }
          .app-nav-links button { padding: 8px 16px !important; font-size: 14px !important; }
        }
        @media (min-width: 1920px) {
          .app-main-pad { max-width: 1760px !important; padding: 40px 80px !important; }
        }
        /* Compact nav for mid-size laptops (960-1200px) */
        @media (max-width: 1200px) {
          .app-nav-links button { padding: 7px 9px !important; font-size: 13px !important; }
          .app-user-label { display: none !important; }
        }
        @media (max-width: 1080px) {
          .app-nav-links button { padding: 6px 7px !important; font-size: 12px !important; }
          .app-signout { font-size: 12px !important; padding: 5px 10px !important; }
        }
        /* Hamburger for tablets and small laptops */
        @media (max-width: 960px) {
          .app-hamburger { display: flex !important; }
          .app-nav-links { display: none !important; }
          .app-mobile-nav { display: flex; flex-direction: column; position: fixed; top: 56px; left: 0; right: 0; background: linear-gradient(to bottom, #0f766e, #6d28d9); z-index: 99; box-shadow: 0 8px 24px rgba(0,0,0,0.2); }
          .app-mobile-nav.closed { display: none; }
          .app-mobile-nav-item { padding: 14px 20px; color: rgba(255,255,255,0.85); font-size: 15px; font-weight: 500; border-bottom: 1px solid rgba(255,255,255,0.08); cursor: pointer; background: none; border-left: none; border-right: none; border-top: none; text-align: left; font-family: ${FONT}; }
          .app-mobile-nav-item:hover, .app-mobile-nav-item.active { background: rgba(255,255,255,0.1); color: white; }
          .app-main-pad { padding: 16px 14px !important; }
        }
        @media (max-width: 480px) {
          .app-main-pad { padding: 12px 10px !important; }
          .demo-banner { padding: 6px 12px !important; gap: 8px !important; flex-wrap: wrap !important; }
          .demo-banner span { font-size: 11px !important; }
          .demo-banner button { padding: 3px 10px !important; font-size: 11px !important; }
        }
      `}</style>

      <Toast toasts={toasts} removeToast={removeToast} />

      {/* Setup wizard — shows once for new admins, renders as full-screen overlay */}
      {showWizard && (
        <SetupWizard
          currentUser={currentUser}
          onComplete={() => {
            localStorage.setItem(`wizard_done_${currentUser.clinic_id || currentUser.id}`, '1')
            setShowWizard(false)
          }}
        />
      )}

      {/* Payment failed banner — shown to admin when Stripe couldn't charge the card */}
      {!isDemo && currentUser.payment_failed && isAdmin && (
        <PaymentFailedBanner clinicId={clinicId} />
      )}

      {/* Demo role-switcher banner */}
      {isDemo && (
        <div className="demo-banner" style={{ background: '#1e293b', padding: '8px 24px', display: 'flex', alignItems: 'center', gap: '12px', fontFamily: FONT }}>
          <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '500', flexShrink: 0 }}>Preview as:</span>
          {['admin', 'therapist', 'front_desk', 'patient'].map(r => {
            const labels = { admin: 'Admin', therapist: 'Therapist', front_desk: 'Front Desk', patient: 'Patient View' }
            const colors = { admin: '#0d9488', therapist: '#1d4ed8', front_desk: '#7c3aed', patient: '#db2777' }
            const active = demoRole === r
            const handleRoleClick = async () => {
              if (r === 'patient') {
                // Fetch the first demo patient and use their ID directly — no separate login needed
                setDemoPatientLoading(true)
                setDemoRole('patient')
                try {
                  const res  = await axios.get(API + '/patients?demo=true')
                  const data = res.data
                  const first = Array.isArray(data) && data[0]
                  if (first) {
                    setDemoPatientUser({ id: null, username: first.name, role: 'patient', patient_id: first.id, clinic_id: null })
                  } else {
                    setDemoPatientUser(null)
                  }
                } catch { setDemoPatientUser(null) }
                finally { setDemoPatientLoading(false) }
              } else {
                setDemoRole(r)
                setDemoPatientUser(null)
                setCurrentPage(r === 'front_desk' ? 'appointments' : 'summary')
              }
            }
            return (
              <button
                key={r}
                onClick={handleRoleClick}
                style={{
                  padding: '4px 12px', borderRadius: '99px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                  background: active ? colors[r] : 'transparent',
                  color: active ? 'white' : '#64748b',
                  border: `1.5px solid ${active ? colors[r] : '#334155'}`,
                  fontFamily: FONT, transition: 'all 0.15s',
                }}
              >{labels[r]}</button>
            )
          })}
        </div>
      )}

      {/* Demo patient view — render full PatientView when demo role is 'patient' */}
      {isDemo && demoRole === 'patient' && (
        demoPatientLoading ? (
          <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT }}>
            <div style={{ textAlign: 'center', color: '#64748b' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid #e2e8f0', borderTop: '2px solid #7c3aed', margin: '0 auto 14px', animation: 'spin .7s linear infinite' }} />
              Loading patient portal…
            </div>
          </div>
        ) : demoPatientUser ? (
          <PatientView user={demoPatientUser} onLogout={() => { setDemoRole('admin'); setDemoPatientUser(null) }} />
        ) : (
          <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT }}>
            <div style={{ textAlign: 'center', color: '#64748b', maxWidth: 340 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}></div>
              <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>Patient portal unavailable</div>
              <div style={{ fontSize: 14, marginBottom: 20 }}>The demo patient account hasn't been set up on the server yet. Run <code>/demo/setup</code> on your EC2 instance to fix this.</div>
              <button onClick={() => setDemoRole('admin')} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontFamily: FONT }}>← Back to Admin</button>
            </div>
          </div>
        )
      )}

      {/*  Top nav bar (hidden while in patient demo view)  */}
      {!(isDemo && demoRole === 'patient') && <>
      <div style={{ background: `linear-gradient(to right, ${TEAL_DARK} 0%, ${PURPLE_DARK} 100%)`, height: '56px', display: 'flex', alignItems: 'center', paddingLeft: '16px', paddingRight: '16px', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 3px rgba(0,0,0,0.15)', gap: 8, overflow: 'hidden' }}>
        {/* Logo */}
        <div style={{ marginRight: '20px', flexShrink: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: 'white', letterSpacing: '-0.01em', lineHeight: 1.2 }}>{clinicName}</div>
          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: '2px' }}>ClearPath PT</div>
        </div>

        {/* Desktop nav links */}
        <div className="app-nav-links">
          {navItems.map(item => (
            <button key={item.id} className="nav-item" onClick={() => setCurrentPage(item.id)} style={{
              padding: '7px 14px', background: currentPage === item.id ? 'rgba(255,255,255,0.15)' : 'transparent',
              border: 'none', borderRadius: '7px', cursor: 'pointer', color: currentPage === item.id ? 'white' : 'rgba(255,255,255,0.65)',
              fontSize: '14px', fontWeight: currentPage === item.id ? '600' : '400',
              whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.15s', fontFamily: FONT,
              borderBottom: currentPage === item.id ? `2px solid ${PURPLE_MID}` : '2px solid transparent',
            }}>
              {item.label}
              {item.badge > 0 && (
                <span style={{ background: '#f87171', color: 'white', borderRadius: '99px', fontSize: '10px', fontWeight: '700', padding: '1px 6px', lineHeight: '1.6' }}>
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </div>


        {/* User info + language switcher + sign out (desktop) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <LanguageSwitcher variant="light" />
          <div className="app-user-label" style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'white', lineHeight: 1.2 }}>{currentUser.username}</div>
            <div style={{ fontSize: '10px', fontWeight: '600', color: roleBadgeColor, lineHeight: 1.2, marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {roleLabel}
            </div>
          </div>
          <button className="app-signout" onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.85)', padding: '6px 14px', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: FONT }}>
            Sign out
          </button>
        </div>

        {/* Hamburger (mobile only) */}
        <button className="app-hamburger" onClick={() => setMobileNavOpen(o => !o)}
          style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: 36, height: 36, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            {mobileNavOpen
              ? <><line x1="3" y1="3" x2="17" y2="17" stroke="white" strokeWidth="2" strokeLinecap="round"/><line x1="17" y1="3" x2="3" y2="17" stroke="white" strokeWidth="2" strokeLinecap="round"/></>
              : <><line x1="2" y1="6" x2="18" y2="6" stroke="white" strokeWidth="2" strokeLinecap="round"/><line x1="2" y1="10" x2="18" y2="10" stroke="white" strokeWidth="2" strokeLinecap="round"/><line x1="2" y1="14" x2="18" y2="14" stroke="white" strokeWidth="2" strokeLinecap="round"/></>
            }
          </svg>
        </button>
      </div>

      {/* Mobile nav drawer */}
      <div className={`app-mobile-nav${mobileNavOpen ? '' : ' closed'}`}>
        {navItems.map(item => (
          <button key={item.id} className={`app-mobile-nav-item${currentPage === item.id ? ' active' : ''}`}
            onClick={() => { setCurrentPage(item.id); setMobileNavOpen(false) }}>
            {item.label}
            {item.badge > 0 && (
              <span style={{ marginLeft: 8, background: '#f87171', color: 'white', borderRadius: '99px', fontSize: '10px', fontWeight: '700', padding: '1px 6px' }}>
                {item.badge}
              </span>
            )}
          </button>
        ))}
        <button className="app-mobile-nav-item" onClick={() => { handleLogout(); setMobileNavOpen(false) }} style={{ color: '#fca5a5' }}>
          Sign out
        </button>
      </div>

      {/* Injury Explorer is full-width — rendered outside the padded container */}
      {currentPage === 'injury-explorer' && (
        <InjuryExplorer patientId={null} patientName={null} />
      )}

      {currentPage !== 'injury-explorer' && (
        <div key={currentUser?.username} className="app-main-pad" style={{ maxWidth: '1280px', margin: '0 auto', padding: '28px 32px' }}>
          {currentPage === 'summary' && !effectiveIsFrontDesk && <ClinicSummary key={currentUser?.username} onSelectPatient={goToPatient} isDemo={isDemo} clinicId={clinicId}
            featureFlags={{ plateau: hasFeature(effectiveUser, 'plateau'), outcomes: hasFeature(effectiveUser, 'outcomes') }}
          />}
          {currentPage === 'list' && (
            <PatientList
              key={currentUser?.username}
              onSelectPatient={goToPatient}
              isDemo={isDemo}
              clinicId={clinicId}
              onDeletePatient={(id, name) => addToast(`${name} has been deleted`, 'success')}
              onViewAsPatient={(p) => setPatientViewUser({ patient_id: p.id, username: p.name, role: 'patient', clinic_id: null })}
            />
          )}
          {currentPage === 'add' && <AddPatient clinicId={clinicId} onSuccess={(emailSent, email) => {
            setCurrentPage('list')
            if (emailSent) addToast(`Patient added! Intake form emailed to ${email}`, 'success')
            else addToast('Patient added successfully', 'success')
          }} />}
          {currentPage === 'dashboard' && <PatientDashboard patient={selectedPatient} onToast={addToast} userRole={effectiveRole} currentUser={currentUser}
            featureFlags={{
              caleb:       hasFeature(effectiveUser, 'caleb'),
              soap_ai:     hasFeature(effectiveUser, 'soap_ai'),
              discharge:   hasFeature(effectiveUser, 'discharge'),
              compliance:  hasFeature(effectiveUser, 'compliance'),
              hep:         hasFeature(effectiveUser, 'hep'),
              pdf:         hasFeature(effectiveUser, 'pdf'),
              plateau:     hasFeature(effectiveUser, 'plateau'),
              outcomes:    hasFeature(effectiveUser, 'outcomes'),
            }}
          />}
          {currentPage === 'create-login' && effectiveIsAdmin && <CreatePatientLogin clinicId={clinicId} />}
          {currentPage === 'staff' && effectiveIsAdmin && (
            canStaff
              ? <StaffManagement clinicId={clinicId} currentUserId={currentUser.id} onToast={addToast} />
              : <FeatureLocked name="Staff Role Management" onUpgrade={() => setCurrentPage('settings')} />
          )}
          {currentPage === 'settings' && effectiveIsAdmin && <Settings clinicId={clinicId} currentUser={currentUser} pendingPriceSelection={pendingPriceSelection} onSave={() => {
            axios.get(API + '/settings?clinic_id=' + clinicId).then(res => setClinicName(res.data.clinic_name || 'ClearPath PT')).catch(() => {})
          }} />}
          {currentPage === 'appointments' && <Appointments clinicId={clinicId} userRole={effectiveRole} isDemo={isDemo} />}
          {currentPage === 'analytics' && !effectiveIsFrontDesk && <Analytics clinicId={clinicId} isDemo={isDemo} />}
        </div>
      )}
    </>}
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LanguageProvider>
          <CalebProvider>
            {/* CalebPanel — only rendered when clinic has a Caleb AI feature */}
            <CalebPanel />
            <Routes>
              <Route path="/intake/:token" element={<ErrorBoundary><IntakeFormWrapper /></ErrorBoundary>} />
              <Route path="/reset-password/:token" element={<ErrorBoundary><ResetPasswordWrapper /></ErrorBoundary>} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/baa" element={<BusinessAssociateAgreement />} />
              <Route path="*" element={<ErrorBoundary><MainApp /></ErrorBoundary>} />
            </Routes>
          </CalebProvider>
        </LanguageProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '5px' }
const inputStyle = { width: '100%', padding: '10px 12px', fontSize: '15px', border: '1.5px solid #e2e8f0', borderRadius: '8px', boxSizing: 'border-box', color: '#1e293b', background: 'white' }

export default App
